import { useMemo, useState } from 'react';
import {
    ColumnDef,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { SORTING_FNS } from 'plugins/DITable/helpers/sort';
import { Box, useDisclosure } from '@chakra-ui/react';
import { useGetClientAliasesQuery, useLazyExportClientAliasesQuery } from 'core/API/productsApi';
import TagsCell from 'plugins/DITable/Cells/tagsCell';
import DefaultColumnFilter from 'plugins/DITable/filters/DefaultColumnFilter';
import { linkStyle } from 'shared/styles';
import { useGetCompanyInfoSelector, useGetUserInfoSelector } from 'core/redux/selectors/authSelectors';
import { useTranslation } from 'helpers/languages/translations';
import { aliasRow } from './types';

const useAliasContainer = () => {
    const translate = useTranslation();

    const [currentAlias, setCurrentAlias] = useState<{ mainAlias: string; aliases: string[] } | null>(null);

    const { data: aliasesData, isError: isGetAliasError, isLoading } = useGetClientAliasesQuery();
    const [exportAliases, { isLoading: isExportLoading }] = useLazyExportClientAliasesQuery();

    const { isOpen: isOpenEdit, onOpen: onOpenEdit, onClose: onCloseEdit } = useDisclosure();
    const { isOpen: isOpenDelete, onOpen: onOpenDelete, onClose: onCloseDelete } = useDisclosure();
    const { isOpen: isOpenAdd, onOpen: onOpenAdd, onClose: onCloseAdd } = useDisclosure();

    const companyInfo = useGetCompanyInfoSelector();
    const userInfo = useGetUserInfoSelector();
    const readonly = !userInfo?.user_groups?.includes(20);

    const data: Readonly<aliasRow[]> = useMemo(() => {
        const aliasesRows: aliasRow[] = [];

        if (!isLoading && !isGetAliasError && aliasesData && aliasesData.aliases) {
            Object.entries(aliasesData.aliases).forEach(([key, aliases]) => {
                if (aliases.length > 0)
                    aliasesRows.push({
                        mainAlias: key,
                        aliases,
                    });
            });
        }

        return aliasesRows;
    }, [isLoading, isGetAliasError, aliasesData]);

    const columns: Readonly<ColumnDef<aliasRow, any>[]> = useMemo(() => {
        const handleClickAlias = (alias: aliasRow) => {
            setCurrentAlias(alias);
            onOpenEdit();
        };

        return [
            {
                header: () => translate('alias.name_split') as string,
                accessorKey: 'mainAlias',
                meta: {
                    width: '30%',
                },
                enableColumnFilter: false,
                cell: (info) => {
                    const original = info.row.original;
                    return (
                        <Box onClick={() => handleClickAlias(original)} {...linkStyle}>
                            {original.mainAlias}
                        </Box>
                    );
                },
            } as ColumnDef<aliasRow, any>,
            {
                header: () => translate('alias.aliases') as string,
                accessorKey: 'aliases',
                cell: TagsCell as any,
                enableColumnFilter: false,
            } as ColumnDef<aliasRow, any>,
        ];
    }, []);

    const defaultColumn = useMemo(
        () => ({
            meta: {
                Filter: DefaultColumnFilter,
            },
        }),
        [],
    );

    const table = useReactTable<aliasRow>({
        data: data as aliasRow[],
        columns: columns as ColumnDef<aliasRow, any>[],
        defaultColumn,
        sortingFns: SORTING_FNS,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const props = useMemo(
        () => ({
            isLoading,
            isGetAliasError,
            aliasesData,
            table,
            companyInfo,
            isExportLoading,
            isOpenAdd,
            currentAlias,
            isOpenEdit,
            isOpenDelete,
            readonly,
            onCloseDelete,
            onOpenDelete,
            onCloseEdit,
            setCurrentAlias,
            onCloseAdd,
            exportAliases,
            onOpenAdd,
        }),
        [
            isLoading,
            isGetAliasError,
            aliasesData,
            table,
            companyInfo,
            isExportLoading,
            isOpenAdd,
            currentAlias,
            isOpenEdit,
            isOpenDelete,
            readonly,
        ],
    );

    return props;
};

export type TAliasProps = ReturnType<typeof useAliasContainer>;
export default useAliasContainer;
